
export default function AboutSection() {
  const stats = [
    { number: "5M+", label: "Global Users", icon: "ri-user-line" },
    { number: "12+", label: "Innovative Products", icon: "ri-apps-line" },
    { number: "80+", label: "Countries Covered", icon: "ri-global-line" },
    { number: "99.9%", label: "Service Availability", icon: "ri-shield-check-line" }
  ];

  const values = [
    {
      icon: "ri-brain-line",
      title: "Revolutionary AI Integration",
      description: "Experience cutting-edge AI-driven social interactions and adaptive gameplay unmatched in any sandbox game."
    },
    {
      icon: "ri-camera-switch-line", 
      title: "Physical-Virtual Bridge",
      description: "Unique video action features that recognize real-world gestures and transform them into magical in-game effects."
    },
    {
      icon: "ri-swords-line",
      title: "Competitive Combat System", 
      description: "Intense PvP battles with strategic depth, live streaming capabilities, and innovative respawn mechanics for continuous combat experience."
    },
    {
      icon: "ri-community-line",
      title: "Unlimited Social Platform",
      description: "A platform that merges creativity, gaming, and social interaction, allowing you to express yourself and connect with others."
    }
  ];

  return (
    <section id="about" className="px-6 py-20">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Why Choose
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400"> Stratava AI</span>
            </h2>
            <p className="text-lg text-white/80 mb-6 leading-relaxed">
              Stratava AI is committed to revolutionizing interactive entertainment experiences through artificial intelligence technology. 
              We believe technology should connect people, not separate them. Our mission is to create innovative applications that 
              foster meaningful connections, inspire creativity, and provide unlimited entertainment possibilities.
            </p>
            <p className="text-lg text-white/80 mb-8 leading-relaxed">
              Founded by a passionate team of technology experts and community builders, we have grown from a small startup to 
              a platform serving millions of users worldwide. Our commitment to excellence drives us to continuously innovate 
              and expand our products.
            </p>
            
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="text-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-2 text-purple-400">
                    <i className={`${stat.icon} text-2xl`}></i>
                  </div>
                  <div className="text-3xl font-bold text-white mb-1">{stat.number}</div>
                  <div className="text-sm text-white/60">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <div className="w-full h-96 bg-cover bg-center rounded-2xl border border-white/20" style={{
              backgroundImage: `url('https://public.readdy.ai/ai/img_res/4c2ac9e0dca4540439672624cf6b327e.jpg')`
            }}></div>
          </div>
        </div>

        <div className="mb-16">
          <h3 className="text-3xl font-bold text-center text-white mb-12">Our Core Advantages</h3>
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-gradient-to-br from-purple-600/20 to-blue-600/20 rounded-2xl p-8 border border-white/20">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mb-6">
                  <i className={`${value.icon} text-2xl text-white`}></i>
                </div>
                <h4 className="text-2xl font-bold text-white mb-4">{value.title}</h4>
                <p className="text-white/80 text-lg leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-2xl p-12 border border-purple-400/30 text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-8">
            <i className="ri-rocket-line text-3xl text-white"></i>
          </div>
          <h3 className="text-4xl lg:text-5xl font-bold text-white mb-8">Coming Soon!</h3>
          <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
            Stratava AI is currently in development. We are creating the most innovative AI-driven social sandbox game 
            that will revolutionize how you play, create, and interact.
          </p>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 mb-8">
            <h4 className="text-2xl font-bold text-white mb-4">🔧 Features We're Building</h4>
            <div className="grid md:grid-cols-2 gap-4 text-left">
              <div className="flex items-center space-x-3">
                <i className="ri-check-line text-green-400 text-xl"></i>
                <span className="text-white/80">Dynamic Voice Chat Rooms</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-check-line text-green-400 text-xl"></i>
                <span className="text-white/80">Video Action Recognition</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-check-line text-green-400 text-xl"></i>
                <span className="text-white/80">Real-time Competitive Combat</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-check-line text-green-400 text-xl"></i>
                <span className="text-white/80">Intelligent AI Characters</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-check-line text-green-400 text-xl"></i>
                <span className="text-white/80">Creative World Building</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-check-line text-green-400 text-xl"></i>
                <span className="text-white/80">Cross-platform Support</span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:from-purple-700 hover:to-blue-700 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-notification-line mr-2"></i>
              Get Launch Notifications
            </button>
            <button className="border-2 border-white/30 text-white px-8 py-3 rounded-full font-semibold hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-mail-line mr-2"></i>
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
